"use client"

import { useState, useEffect, Suspense } from "react"
import { useSearchParams } from "next/navigation"
import Image from "next/image"
import { Button, Input, Card } from "@repo/ui"
import { logLoginFailed } from "@/app/actions/auth"
import { PasswordInput } from "@/components/auth"
import { logger } from '@/lib/services/logger.service'

function LoginForm() {
  const searchParams = useSearchParams()

  // Initialize email from localStorage if available
  const [email, setEmail] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('remembered-email') || ""
    }
    return ""
  })
  const [password, setPassword] = useState("")
  const [rememberMe, setRememberMe] = useState(() => {
    // Default to true if we have a remembered email
    if (typeof window !== 'undefined') {
      return !!localStorage.getItem('remembered-email')
    }
    return true
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Check for error/session parameters from middleware redirect
  useEffect(() => {
    const errorParam = searchParams.get('error')
    const sessionParam = searchParams.get('session')

    if (errorParam === 'unauthorized') {
      setError('You do not have permission to access the therapist portal. Please contact support if you believe this is an error.')
    } else if (sessionParam === 'expired') {
      setError('Your session has expired. Please sign in again.')
    }
  }, [searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    logger.debug('[Login] Form submitted')
    
    // Basic validation
    if (!email || !password) {
      setError("Please enter both email and password")
      return
    }
    
    setIsLoading(true)
    logger.debug('[Login] Starting sign in...')
    
    try {
      // Use server-side login API
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Important: send cookies
        body: JSON.stringify({ email, password })
      })

      const data = await response.json()
      logger.debug('[Login] Sign in completed', { success: data.success, hasMFA: data.hasMFA })

      if (!response.ok || !data.success) {
        const errorMessage = data.error || 'Login failed'

        // Invalid credentials is expected user behavior — the audit log
        // below captures it for brute-force detection. Only surface real
        // server failures (5xx, network) to error_logs.
        const isExpectedAuthError = /invalid login credentials/i.test(errorMessage)
        if (!isExpectedAuthError) {
          logger.error(`Login failed: ${errorMessage}`, new Error(errorMessage), {
            component: 'LoginPage',
            action: 'handleLogin',
            email: email.substring(0, email.indexOf('@')) + '@***',
            errorCode: 'AUTH_ERROR'
          })
        }

        // Non-blocking audit log via server action
        logLoginFailed(email, errorMessage).catch(err => {
          console.error('[Login] Failed to log failed login attempt:', err)
        })

        setError(errorMessage)
        setIsLoading(false)
        return
      }

      // Save or remove email based on remember me preference
      if (typeof window !== 'undefined') {
        if (rememberMe) {
          localStorage.setItem('remembered-email', email)
        } else {
          localStorage.removeItem('remembered-email')
        }
      }

      setPassword("")

      // Post-auth destination: respect ?returnTo when the middleware
      // sent the user here from a protected route (magic-link deep
      // links, etc). Same-origin-only guard: reject anything that
      // doesn't start with `/` or contains `//` — no open-redirect.
      const rawReturnTo = searchParams.get('returnTo')
      const safeReturnTo = rawReturnTo && rawReturnTo.startsWith('/') && !rawReturnTo.startsWith('//')
        ? rawReturnTo
        : null

      // Check if user has MFA enabled (returned from server)
      if (data.hasMFA && data.mfaFactorId && data.mfaChallengeId) {
        // User has MFA enabled, use full page navigation to /mfa/verify
        // ProfileLoader will automatically load the profile after successful verification.
        // Thread returnTo through so the MFA verify page can bounce them onward.
        const mfaUrl = new URL('/mfa/verify', window.location.origin)
        mfaUrl.searchParams.set('factorId', data.mfaFactorId)
        mfaUrl.searchParams.set('challengeId', data.mfaChallengeId)
        if (safeReturnTo) mfaUrl.searchParams.set('returnTo', safeReturnTo)
        window.location.href = mfaUrl.pathname + mfaUrl.search
        return
      }

      // No MFA - honour returnTo, otherwise land on /schedule.
      logger.debug('[Login] No MFA, redirecting', { authId: data.user.id, returnTo: safeReturnTo })
      window.location.href = safeReturnTo ?? '/schedule'
    } catch (err) {
      setError("An unexpected error occurred. Please try again.")
      // Network failures during login (Safari "Load failed", Chrome
      // "Failed to fetch", user offline) are user-side network issues —
      // downgrade to warn so they don't pollute the error viewer. Real
      // server-side login failures come back as non-2xx responses and
      // are handled above with typed messages.
      const errMessage = (err as any)?.message || ''
      const isNetworkError =
        err instanceof TypeError &&
        (errMessage === 'Load failed' || errMessage === 'Failed to fetch' ||
         errMessage.includes('NetworkError'))
      const logContext = {
        component: 'LoginPage',
        action: 'handleLogin',
        email: email.substring(0, email.indexOf('@')) + '@***', // Partially mask email
        errorDetails: {
          message: errMessage || 'Unknown error',
          code: (err as any)?.code || 'UNEXPECTED_ERROR',
          name: (err as any)?.name,
          stack: (err as any)?.stack
        }
      }
      if (isNetworkError) {
        logger.warn('Login fetch failed (network)', logContext)
      } else {
        logger.error('Unexpected login error', err, logContext)
      }
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mt-8">
          <Image
            src="/alli_logo.png"
            alt="Alli"
            width={360}
            height={144}
            priority
            className="w-36"
            style={{ height: 'auto' }}
          />
        </div>
        <h2 className="text-center text-3xl font-extrabold text-gray-900">
          Welcome back
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Sign in to access your therapist portal
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card variant="elevated" className="px-4 py-8 sm:px-10 bg-white/80 backdrop-blur-sm">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            <Input
              label="Email address"
              type="email"
              id="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
            />

            <PasswordInput
              label="Password"
              value={password}
              onChange={setPassword}
              placeholder="Enter your password"
              id="password"
              name="password"
              autoComplete="current-password"
              required
            />

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setRememberMe(checked)
                    // If unchecking, remove saved email
                    if (!checked && typeof window !== 'undefined') {
                      localStorage.removeItem('remembered-email')
                    }
                  }}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 cursor-pointer select-none">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <a href="/forgot-password" className="font-medium text-primary-600 hover:text-primary-700 transition-colors">
                  Forgot password?
                </a>
              </div>
            </div>

            <div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full transition-all hover:shadow-lg"
                isLoading={isLoading}
              >
                Sign in
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  )
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    }>
      <LoginForm />
    </Suspense>
  )
}